import { useCallback, useEffect, useState } from 'react'

import { Text } from '@taro/design'
import { InkReveal } from '@taro/ink/react'
import { initDataRaw } from '@taro/tg'

import { ApiError, api, type Proof as ProofData, type Reading as ReadingData, type Spread } from '../api'
import { Proof } from './reading/Proof'
import { Shuffle } from './reading/Shuffle'
import { SpreadIntro } from './reading/SpreadIntro'
import { SpreadList } from './reading/SpreadList'
import { Table } from './reading/Table'

type Step =
  | { kind: 'catalog' }
  | { kind: 'intro'; spread: Spread }
  | { kind: 'shuffle'; reading: ReadingData }
  | { kind: 'table'; reading: ReadingData }
  | { kind: 'proof'; reading: ReadingData; proof: ProofData }

interface Props {
  balance: number
  onBalance: (value: number) => void
}

/**
 * Расклад целиком: каталог → вопрос → тасовка → стол → проверка.
 *
 * Машина состояний живёт здесь, а не в роутере: шаги связаны не адресами,
 * а данными — на тасовку нельзя попасть без созданного расклада, а на
 * проверку без раздачи. Роутер такие связи не выражает, и первый же переход
 * «назад» в браузере оставил бы человека на экране веера без колоды.
 *
 * Переходы между шагами — чернильные (уровень `screen`); всё, что появляется
 * внутри шага, дышит. Это то самое решение из docs/plan/12-ink-module.md:
 * громкость эффекта задаётся уровнем, а не вкусом.
 */
export function Reading({ balance, onBalance }: Props) {
  const [spreads, setSpreads] = useState<Spread[] | null>(null)
  const [step, setStep] = useState<Step>({ kind: 'catalog' })
  const [shown, setShown] = useState<Step>({ kind: 'catalog' })
  const [visible, setVisible] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api.spreads(initDataRaw()).then(
      (list) => live && setSpreads(list),
      (e: Error) => live && setError(e.message),
    )
    return () => {
      live = false
    }
  }, [])

  // Смена шага идёт в два такта: сначала уходит старый экран, потом
  // приходит новый. Одновременно они не живут — иначе два расклада
  // накладывались бы друг на друга посреди перехода.
  useEffect(() => {
    if (step !== shown) setVisible(false)
  }, [step, shown])

  const go = useCallback((next: Step) => {
    setError(null)
    setStep(next)
  }, [])

  const start = useCallback(
    async (spread: Spread, question: string) => {
      setBusy(true)
      setError(null)
      try {
        const reading = await api.createReading(initDataRaw(), spread.slug, question)
        go({ kind: 'shuffle', reading })
      } catch (e) {
        setError(message(e))
      } finally {
        setBusy(false)
      }
    },
    [go],
  )

  const draw = useCallback(
    async (reading: ReadingData, clientSeed: string, picks: number[]) => {
      setBusy(true)
      setError(null)
      try {
        const drawn = await api.drawReading(initDataRaw(), reading.id, clientSeed, picks)
        onBalance(drawn.balance)
        go({ kind: 'table', reading: drawn })
      } catch (e) {
        setError(message(e))
      } finally {
        setBusy(false)
      }
    },
    [go, onBalance],
  )

  const verify = useCallback(
    async (reading: ReadingData) => {
      try {
        const proof = await api.verifyReading(initDataRaw(), reading.id)
        go({ kind: 'proof', reading, proof })
      } catch (e) {
        setError(message(e))
      }
    },
    [go],
  )

  if (error !== null && shown.kind === 'catalog' && spreads === null) {
    return (
      <Text variant="body-m" tone="tertiary">
        {error}
      </Text>
    )
  }

  return (
    <InkReveal
      show={visible}
      scope="screen"
      onHidden={() => {
        setShown(step)
        setVisible(true)
      }}
    >
      {render()}
    </InkReveal>
  )

  function render() {
    switch (shown.kind) {
      case 'catalog':
        return spreads === null ? (
          <Text variant="body-m" tone="tertiary">
            Раскладываем колоду…
          </Text>
        ) : (
          <SpreadList
            spreads={spreads}
            balance={balance}
            onPick={(spread) => go({ kind: 'intro', spread })}
          />
        )

      case 'intro':
        return (
          <SpreadIntro
            spread={shown.spread}
            balance={balance}
            busy={busy}
            error={error}
            onStart={(question) => void start(shown.spread, question)}
            onBack={() => go({ kind: 'catalog' })}
          />
        )

      case 'shuffle':
        return (
          <Shuffle
            reading={shown.reading}
            busy={busy}
            error={error}
            onDraw={(seed, picks) => void draw(shown.reading, seed, picks)}
          />
        )

      case 'table':
        return (
          <Table
            reading={shown.reading}
            onVerify={() => void verify(shown.reading)}
            onAgain={() => go({ kind: 'catalog' })}
          />
        )

      case 'proof':
        return (
          <Proof
            proof={shown.proof}
            onBack={() => go({ kind: 'table', reading: shown.reading })}
          />
        )
    }
  }
}

/** Текст ошибки для человека. Код разбирает вызывающий, если ему нужно. */
function message(e: unknown): string {
  if (e instanceof ApiError) return e.message
  return (e as Error).message ?? 'Что-то пошло не так.'
}
